import { useState, useEffect } from "react";
import { USER_ROLES, TENANTS } from "../config/constants";
import {
  logout as apiLogout,
  userForStorage,
  getProfile,
  restoreSession,
} from "../services/authService";

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(USER_ROLES[0]);
  const [currentTenantId, setCurrentTenantId] = useState("circuitflow");
  const [isInitializing, setIsInitializing] = useState(true);

  // Restore session on mount (refresh access token when expired but remember-me refresh is valid)
  useEffect(() => {
    let cancelled = false;

    const applyStoredUser = (storedUser) => {
      const roleMap = {
        super_admin: "Super Admin",
        admin: "Admin",
        viewer: "Read Only",
      };
      const mappedRole = roleMap[storedUser.role] || "Read Only";
      const tenantSlug = storedUser.tenantSlug || "circuitflow";

      setIsAuthenticated(true);
      setUser(storedUser);
      setUserRole(mappedRole);
      setCurrentTenantId(tenantSlug);

      localStorage.setItem(
        "auth",
        JSON.stringify({
          isAuthenticated: true,
          user: userForStorage(storedUser),
          userRole: mappedRole,
          tenantId: tenantSlug,
        })
      );
    };

    (async () => {
      sessionStorage.removeItem("auth_failed");
      const result = await restoreSession();
      if (cancelled) return;

      if (result.success && result.user) {
        applyStoredUser(result.user);
      } else {
        setIsAuthenticated(false);
      }
      setIsInitializing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // After init, fetch full profile (including tenant logo) for display; keep logo in memory only
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    getProfile().then((result) => {
      if (result.success && result.user) setUser(result.user);
    }).catch(() => {});
  }, [isAuthenticated]);

  const refreshProfile = async () => {
    const result = await getProfile();
    if (result.success && result.user) setUser(result.user);
  };

  const login = (role, tenantId, userData) => {
    const authState = {
      isAuthenticated: true,
      user: userForStorage(userData),
      userRole: role,
      tenantId: tenantId,
    };
    
    // Update state
    setUserRole(role);
    setCurrentTenantId(tenantId);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Save to localStorage (for backward compatibility; omit logo to avoid QuotaExceededError)
    localStorage.setItem("auth", JSON.stringify(authState));
    
    // Note: Access token and user are already saved by authService.login()
    console.log("✅ User logged in successfully:", userData.email);
  };

  const logout = async () => {
    try {
      // Call API logout to clear server-side session if needed
      await apiLogout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    // Clear state
    setIsAuthenticated(false);
    setUser(null);
    setUserRole(USER_ROLES[0]);
    setCurrentTenantId("circuitflow");
    
    // Clear legacy auth from localStorage
    localStorage.removeItem("auth");
    
    console.log("✅ User logged out successfully");
    
    // Force page refresh to ensure clean state
    window.location.href = '/';
  };

  // Get current tenant from TENANTS or provide a default fallback; use tenant logo from API when available
  const defaultLogoSvg = 'data:image/svg+xml;utf8,<svg width="180" height="70" viewBox="0 0 180 70" xmlns="http://www.w3.org/2000/svg"><g transform="translate(75 10) scale(1.4)"><path fill="%2360A5FA" d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7zM9 21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-1H9v1z"/></g><text x="90" y="55" font-family="IBM Plex Sans, Segoe UI, Helvetica, Arial, sans-serif" font-size="18" font-weight="700" fill="%231F2937" text-anchor="middle" transform="translate(0 5)">Inventory</text></svg>';
  const baseTenant = TENANTS[currentTenantId] || {
    id: user?.tenantId || null,
    name: user?.tenantName || 'Default Tenant',
    logo: defaultLogoSvg,
    primaryColor: "blue-500",
    primaryBgColor: "bg-blue-500",
    primaryTextColor: "text-blue-500",
  };
  const currentTenant = {
    ...baseTenant,
    logo: user?.tenantLogo || baseTenant.logo,
  };

  return {
    isAuthenticated,
    user,
    userRole,
    currentTenantId,
    currentTenant,
    tenantId: user?.tenantId || null, // Expose the actual database tenant ID
    isInitializing,
    login,
    logout,
    refreshProfile,
  };
};
